import {
  ignoreAbortError,
  onAbort,
  resolveOnAbort,
  throwIfAborted,
} from '@/utils/abort-util'
import { type TeamStore } from './TeamStore'
import { NewTeamModalViewModel } from '@/features/iam/teams/NewTeamModal/NewTeamModalViewModel'
import { type GetCurrentMemberFn } from '@/features/iam'
import { type TeamMemberStore } from './TeamMemberStore'
import { TeamListViewModel } from '@/features/iam/teams/TeamListViewModel'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { computed } from 'mobx'
import { task } from 'mobx-task'
import { type Team } from './Team'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

export class TeamsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The visit's request for the user's teams, shared by the team route's
   * loader and {@link activate} so that a visit fetches them once.
   */
  private _visitFetch: Promise<void> | null = null

  readonly newTeamVm: NewTeamModalViewModel
  readonly teamListVm: TeamListViewModel

  constructor(
    private readonly _workspace: Workspace,
    private readonly _teamMemberStore: TeamMemberStore,
    private readonly _teamStore: TeamStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    this.newTeamVm = new NewTeamModalViewModel(
      this._teamMemberStore,
      this._teamStore,
      this._workspace,
    )

    this.teamListVm = new TeamListViewModel(() => this.teams)

    this._lifecycle.manage(this.teamListVm)
  }

  @computed
  get teams() {
    return this._teamMemberStore
      .teamMembersForWorkspaceMember()
      .map((m) => m.team)
  }

  @computed
  get canCreateTeam() {
    return this._getCurrentMember().permissions.createTeam
  }

  @computed
  get canViewTeams() {
    const { permissions } = this._getCurrentMember()
    return permissions.createTeam || permissions.adminTeamsView
  }

  /**
   * The team to show when the route names none.
   */
  @computed
  get defaultTeam(): Team | null {
    return this.teams[0] ?? null
  }

  activate() {
    this._lifecycle.activate()
    void this.fetchForVisit().catch(ignoreAbortError())
  }

  deactivate() {
    this._lifecycle.deactivate()
    this._visitFetch = null
  }

  /**
   * Fetches the user's teams, unless this visit already has.
   *
   * @param signal Stops waiting; the request itself carries on for the view.
   */
  async fetchForVisit(signal?: AbortSignal): Promise<void> {
    this._visitFetch ??= this.fetch()
    // A loader abandoned before the view activated would otherwise hand its
    // data to the next visit, which then skips its fetch.
    const unsubscribe = onAbort(signal, () => {
      if (!this._lifecycle.active) {
        this._visitFetch = null
      }
    })
    try {
      throwIfAborted(await resolveOnAbort(this._visitFetch, signal))
    } finally {
      unsubscribe()
    }
  }

  fetch = task(async () => {
    await this._teamMemberStore.listUserTeamMembersForWorkspace({})
  })

  /**
   * Finds the user's team with the slug.
   *
   * @param slug
   */
  findTeam(slug: string): Team | undefined {
    return this.teams.find((t) => t.slug === slug)
  }
}
